import { Check, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { OrderStatus, ORDER_STATUS_LABELS } from "@/types/tms";
import {
  PARTIALLY_CONFIRMED,
  resolveOrderRollupStatus,
  type RollupLeg,
  type RollupStatus,
} from "@/lib/order-rollup-status";

interface OrderStatusBadgeProps {
  status: OrderStatus;
  className?: string;
  /** When true, renders the chip in warning styling with a leading clock icon. */
  late?: boolean;
  /**
   * Leg statuses for this order. When supplied, the chip shows the rollup
   * derived from the legs (including "Partially Confirmed"). Omit it and the
   * chip renders `status` exactly as before.
   */
  legs?: RollupLeg[] | null;
}

const STATUS_CLASSES: Record<RollupStatus, string> = {
  booked: "status-booked",
  confirmed: "status-confirmed",
  in_transit: "status-in-transit",
  delivered: "status-delivered",
  canceled: "status-canceled",
  tender_received: "status-tender-received",
  [PARTIALLY_CONFIRMED]: "status-partially-confirmed",
};

const STATUS_LABELS: Record<RollupStatus, string> = {
  ...ORDER_STATUS_LABELS,
  [PARTIALLY_CONFIRMED]: "Partially Confirmed",
};

export function OrderStatusBadge({ status, className, late = false, legs }: OrderStatusBadgeProps) {
  const resolved: RollupStatus =
    legs && legs.length > 0 ? resolveOrderRollupStatus(status, legs) : status;

  return (
    <span className={cn("status-badge", late ? "status-warning" : STATUS_CLASSES[resolved], className)}>
      {late && <Clock className="mr-1 h-3 w-3" strokeWidth={2.5} />}
      {resolved === "delivered" && <Check className="mr-1 h-3 w-3" strokeWidth={2.5} />}
      {STATUS_LABELS[resolved]}
    </span>
  );
}
